import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { useTranslation } from 'core/i18n/i18n';
import { tenantUserKeys } from 'core/query-client/keys/tenant-users.keys';
import { userLocationAssignmentKeys } from 'core/query-client/keys/user-location-assignment.keys';
import { updateQuery } from 'core/query-client/query-client';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import { USER_LOCATION_ASSIGNMENT_I18N_PREFIX } from 'modules/settings/pods/settings-user-location-assignment/settings-user-location-assignment.constants';
import { replaceBulkUserLocations } from '../user-locations.service';

interface ReplaceUserLocationsOptions {
    userIds: string[];
    onSaved: VoidFunction;
}

/**
 * Full-set replace applied to every selected user in a single atomic write, so a rejection leaves
 * every one of them untouched (POC-20594).
 */
export const useReplaceUserLocations = ({ userIds, onSaved }: ReplaceUserLocationsOptions) => {
    const { t } = useTranslation();

    // Detail only. The list refresh belongs to the page on success — invalidating it here as well
    // would cancel the page's refetch and issue a second read of the same endpoint.
    const refreshAssignments = () => {
        userIds.forEach((userId) => updateQuery(userLocationAssignmentKeys.detail(userId)));
    };

    const { mutate, isPending } = usePocMutation({
        mutationFn: (locationIds: string[]) => replaceBulkUserLocations(userIds, locationIds),
        onSuccess: () => {
            refreshAssignments();
            showSuccessNotification(t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.restrict-locations.success`));
            onSaved();
        },
        // The dialog stays open on a failure, so the page does not refetch and the invalidation
        // belongs here: the save may have been rejected because someone else moved the assignment on.
        onError: () => {
            refreshAssignments();
            updateQuery(tenantUserKeys.all);
            showErrorNotification(t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.restrict-locations.error`));
        },
    });

    return { save: mutate, isSaving: isPending };
};
